import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, Ruler, Waypoints, Move, Compass, FileCheck2, ShieldCheck, Calendar,
  Plane, TrainFront, Route, Briefcase, Camera, Flag, HelpCircle, Heart, ChevronLeft, ChevronRight, IndianRupee
} from "lucide-react";
import { api, priceLabel, inr, fmtDate, formatApiErrorDetail } from "@/lib/loop";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DocStatusRow, VerificationBadge } from "@/components/Badges";
import PropertyPassport from "@/components/PropertyPassport";
import { ChatDialog } from "@/components/Chat";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const DIST_ICONS = { Airport: Plane, "Railway Station": TrainFront, NH: Route, "Business District": Briefcase };
const BADGES = [
  { h: "OWNER VERIFIED", t: "Identity verified against submitted proof." },
  { h: "AUTHORIZED AGENT", t: "Agent has authorization to market the property." },
  { h: "LOCATION VERIFIED", t: "Property location confirmed on site / map." },
  { h: "DOCUMENTS UPLOADED", t: "Supporting documents supplied by the lister." },
  { h: "PROFESSIONAL REVIEW", t: "Documents reviewed by an appropriately qualified professional." },
];

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => nav("/search"));
  }, [id, nav]);

  useEffect(() => {
    if (!user || !p) { setSaved(false); return; }
    api.get("/my/saved").then((r) => setSaved((r.data || []).some((s) => s.id === p.id))).catch(() => {});
  }, [user, p]);

  if (!p) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading property…</div>;

  const facts = [
    { Icon: Ruler, k: "Area", v: p.area_display || `${p.area_sqft} sqft` },
    { Icon: Waypoints, k: "Road", v: p.road_width || "—" },
    { Icon: Move, k: "Frontage", v: p.frontage || "—" },
    { Icon: Compass, k: "Facing", v: p.facing || "—" },
    { Icon: ShieldCheck, k: "Ownership", v: p.ownership || "—" },
    { Icon: FileCheck2, k: "RERA", v: p.rera_status === "registered" ? "Registered" : "Not applicable" },
  ];

  const cost = p.true_cost || {};
  const costRows = [
    ["Property asking price", p.price],
    ["Registration / stamp duty", cost.registration],
    ["GST where applicable", cost.gst],
    ["Maintenance / deposit", cost.maintenance],
    ["Other disclosed charges", cost.other],
  ];
  const total = [p.price, cost.registration, cost.gst, cost.maintenance, cost.other]
    .reduce((s, v) => s + (Number(v) || 0), 0);

  const toggleSave = async () => {
    if (!user) return nav("/auth");
    try {
      if (saved) { await api.delete(`/saved/${p.id}`); setSaved(false); toast("Removed from shortlist"); }
      else { await api.post(`/saved/${p.id}`); setSaved(true); toast.success("Saved to shortlist"); }
    } catch { toast.error("Please sign in"); }
  };

  return (
    <div className="min-h-screen pb-28 md:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6">
        {/* GALLERY */}
        <div className="relative rounded-3xl overflow-hidden aspect-[16/9] bg-[#0B132B]">
          <img src={p.images?.[img]} alt={p.title} className="w-full h-full object-cover" />
          <div className="absolute top-4 left-4 flex gap-2">
            <VerificationBadge status={p.verification_status} testid="detail-verif-badge" />
          </div>
          <div className="absolute bottom-4 left-4 bg-black/60 text-white text-[11px] font-mono-loop px-3 py-1 rounded flex items-center gap-2">
            <Camera className="w-3 h-3" /> Captured / Updated: {p.image_dates?.[img] || fmtDate(p.last_verified)}
          </div>
          {p.images?.length > 1 && (
            <>
              <button data-testid="gallery-prev" onClick={() => setImg((img - 1 + p.images.length) % p.images.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-2 rounded-full text-white"><ChevronLeft /></button>
              <button data-testid="gallery-next" onClick={() => setImg((img + 1) % p.images.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-2 rounded-full text-white"><ChevronRight /></button>
            </>
          )}
        </div>

        {/* HEADER */}
        <div className="mt-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{p.title}</h1>
            <p className="mt-2 flex items-center gap-1.5 text-slate-400">
              <MapPin className="w-4 h-4 text-[#C5A059]" /> {p.locality}, {p.city}, {p.state}
            </p>
            <div className="mt-1 font-mono-loop text-xs text-slate-500">{p.loop_id}</div>
          </div>
          <div className="text-left lg:text-right">
            <div className="text-3xl font-extrabold text-white">{priceLabel(p)}</div>
            {p.price_per_sqft && <div className="text-sm text-[#C5A059]">{inr(p.price_per_sqft)}/sqft</div>}
            <div className="mt-3 flex gap-2">
              <Button data-testid="save-property-btn" onClick={toggleSave} variant="outline"
                className={`border-white/15 ${saved ? "text-rose-400" : "text-slate-200"}`}>
                <Heart className={`w-4 h-4 mr-2 ${saved ? "fill-rose-400" : ""}`} /> {saved ? "Saved" : "Save"}
              </Button>
              <ReportDialog propertyId={p.id} user={user} nav={nav} />
            </div>
          </div>
        </div>

        {/* QUICK FACTS */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {facts.map((f) => (
            <div key={f.k} className="bg-[#0B132B] border border-white/10 rounded-xl p-4">
              <f.Icon className="w-4 h-4 text-[#C5A059]" />
              <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-500">{f.k}</div>
              <div className="text-sm font-semibold text-white">{f.v}</div>
            </div>
          ))}
        </div>

        {/* PASSPORT */}
        <div className="mt-10"><PropertyPassport p={p} /></div>

        {/* TABS */}
        <Tabs defaultValue="documents" className="mt-10">
          <TabsList className="bg-[#0B132B] border border-white/10 flex-wrap h-auto">
            {[["documents", "Documents"], ["cost", "True Buying Cost"], ["location", "Location Intelligence"],
              ["neighbourhood", "Neighbourhood"], ["reality", "Reality View"], ["timeline", "History"], ["badges", "Badges"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#070C1E] text-slate-300">{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="documents" className="mt-6">
            <div className="bg-[#0B132B] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Document Status</h3>
                <span className="text-sm text-[#C5A059] font-medium">
                  {(p.documents || []).filter((d) => d.status !== "not_provided").length} of {(p.documents || []).length} categories supplied
                </span>
              </div>
              {(p.documents || []).map((d, i) => <DocStatusRow key={i} category={d.category} status={d.status} testid={`doc-row-${i}`} />)}
              <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                A document being uploaded does not constitute confirmation of clear title. Documents marked
                "Reviewed by professional" have been examined by an appropriately qualified professional. Buyers must
                obtain independent legal review before any transaction.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="cost" className="mt-6">
            <div className="bg-[#0B132B] border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><IndianRupee className="w-5 h-5 text-[#C5A059]" /> True Buying Cost</h3>
              <div className="mt-4">
                {costRows.map(([label, val], i) => (
                  <div key={i} className="flex justify-between py-3 border-b border-white/5">
                    <span className="text-sm text-slate-300">{label}</span>
                    <span className="text-sm font-medium text-white">{val ? inr(val) : "Not disclosed"}</span>
                  </div>
                ))}
                <div className="flex justify-between py-4 mt-2 bg-[#C5A059]/10 rounded-xl px-4">
                  <span className="text-sm font-bold text-white">Estimated acquisition cost</span>
                  <span className="text-lg font-extrabold text-[#E6C687]">{total ? inr(total) : "Contact for price"}</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-500">Figures are estimates and should be independently confirmed before transaction.</p>
            </div>
          </TabsContent>

          <TabsContent value="location" className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(p.distances || {}).map(([k, v]) => {
                const Icon = DIST_ICONS[k] || MapPin;
                return (
                  <div key={k} className="bg-[#0B132B] border border-white/10 rounded-2xl p-5">
                    <Icon className="w-5 h-5 text-[#C5A059]" />
                    <div className="mt-3 text-sm text-slate-400">{k}</div>
                    <div className="text-xl font-bold text-white">{v}</div>
                  </div>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-slate-500">Distances are indicative. Confirm with an on-site visit and map verification.</p>
          </TabsContent>

          <TabsContent value="neighbourhood" className="mt-6">
            <div className="bg-[#0B132B] border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">Understand the Neighbourhood</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                {Object.entries(p.neighbourhood || {}).map(([k, v]) => (
                  <div key={k} className="py-3 border-b border-white/5">
                    <div className="text-xs uppercase tracking-wider text-[#C5A059]">{k}</div>
                    <div className="text-sm text-slate-300 mt-0.5">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="reality" className="mt-6">
            <h3 className="text-lg font-bold text-white mb-4">See the property. See the reality.</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(p.images || []).map((src, i) => (
                <div key={i} className="relative rounded-2xl overflow-hidden">
                  <img src={src} alt="" className="w-full h-64 object-cover" />
                  <div className="absolute bottom-3 left-3 bg-black/60 text-white text-[11px] font-mono-loop px-3 py-1 rounded">
                    Captured: {p.image_dates?.[i] || "—"}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="mt-6">
            <div className="bg-[#0B132B] border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-6">Property Timeline</h3>
              <div className="relative border-l-2 border-[#C5A059]/30 ml-3 pl-6 space-y-6">
                {(p.timeline || []).slice().reverse().map((t, i) => (
                  <div key={i} className="relative">
                    <span className="absolute -left-[31px] top-1 w-3 h-3 rounded-full bg-[#C5A059]" />
                    <div className="text-sm font-semibold text-white">{t.event}</div>
                    <div className="text-xs text-slate-500">{fmtDate(t.date)}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="badges" className="mt-6">
            <Accordion type="single" collapsible className="bg-[#0B132B] border border-white/10 rounded-2xl px-6">
              {BADGES.map((b, i) => (
                <AccordionItem key={i} value={`b${i}`} className="border-white/5">
                  <AccordionTrigger data-testid={`badge-explain-${i}`} className="text-white hover:no-underline">
                    <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#C5A059]" /> {b.h}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400">{b.t}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
              <HelpCircle className="w-3.5 h-3.5" /> A badge always defines exactly what was checked — never a blanket guarantee.
            </p>
          </TabsContent>
        </Tabs>
      </div>

      {/* Desktop enquiry bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 hidden md:flex flex-wrap gap-3 pb-16">
        <EnquiryDialog property={p} user={user} nav={nav} type="site_visit" label="Schedule Site Visit" primary />
        <ChatDialog property={p} user={user} nav={nav} />
        <EnquiryDialog property={p} user={user} nav={nav} type="contact" label="Request Owner Contact" />
        <EnquiryDialog property={p} user={user} nav={nav} type="documents" label="Request Documents" />
        <EnquiryDialog property={p} user={user} nav={nav} type="question" label="Ask a Question" />
      </div>

      {/* Mobile sticky CTA */}
      <div className="fixed bottom-14 left-0 right-0 z-40 md:hidden bg-[#070C1E]/95 backdrop-blur-xl border-t border-white/10 p-3 flex gap-2">
        <EnquiryDialog property={p} user={user} nav={nav} type="site_visit" label="Site Visit" primary mobile />
        <ChatDialog property={p} user={user} nav={nav} mobile />
        <EnquiryDialog property={p} user={user} nav={nav} type="contact" label="Contact" mobile />
      </div>

      <Footer />
    </div>
  );
}

function EnquiryDialog({ property, user, nav, type, label, primary, mobile }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ message: "", visit_date: "", visit_time: "", visitors: 1, contact_method: "phone" });
  const submit = async () => {
    if (!user) { nav("/auth"); return; }
    try {
      await api.post("/enquiries", { property_id: property.id, type, ...form });
      toast.success("Enquiry sent. The owner will respond via LOOP.");
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`${mobile ? "m-" : ""}enquiry-btn-${type}`}
          className={`${mobile ? "flex-1" : ""} ${primary ? "bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold" : "bg-transparent border border-white/15 text-slate-200 hover:bg-white/5"}`}>
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0B132B] border-white/10 text-white">
        <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Your contact details stay private. Only this owner is notified — no broker spam.</p>
        {type === "site_visit" && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300 text-xs">Date</Label>
              <Input data-testid="visit-date" type="date" value={form.visit_date}
                onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className="mt-1 bg-[#131B35] border-white/10 text-white" /></div>
            <div><Label className="text-slate-300 text-xs">Time</Label>
              <Input data-testid="visit-time" type="time" value={form.visit_time}
                onChange={(e) => setForm({ ...form, visit_time: e.target.value })} className="mt-1 bg-[#131B35] border-white/10 text-white" /></div>
            <div><Label className="text-slate-300 text-xs">Visitors</Label>
              <Input type="number" min="1" value={form.visitors}
                onChange={(e) => setForm({ ...form, visitors: Number(e.target.value) })} className="mt-1 bg-[#131B35] border-white/10 text-white" /></div>
            <div><Label className="text-slate-300 text-xs">Contact via</Label>
              <Select value={form.contact_method} onValueChange={(v) => setForm({ ...form, contact_method: v })}>
                <SelectTrigger className="mt-1 bg-[#131B35] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="phone">Phone</SelectItem><SelectItem value="email">Email</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent>
              </Select></div>
          </div>
        )}
        <Textarea data-testid="enquiry-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Add a message (optional)" className="bg-[#131B35] border-white/10 text-white" />
        <DialogFooter>
          <Button data-testid="enquiry-submit" onClick={submit} className="bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold w-full">Send Enquiry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ propertyId, user, nav }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const reasons = ["Fake listing", "Incorrect price", "Property unavailable", "Wrong location", "Misleading photos", "Duplicate listing", "Unauthorized listing", "Other"];
  const submit = async () => {
    if (!user) return nav("/auth");
    try { await api.post("/reports", { property_id: propertyId, reason, detail }); toast.success("Report submitted. Thank you."); setOpen(false); }
    catch (e) { toast.error("Could not submit report"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="report-btn" variant="ghost" size="icon" className="text-slate-400 hover:text-rose-400"><Flag className="w-4 h-4" /></Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0B132B] border-white/10 text-white">
        <DialogHeader><DialogTitle>Report this property</DialogTitle></DialogHeader>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger data-testid="report-reason" className="bg-[#131B35] border-white/10 text-white"><SelectValue placeholder="Select a reason" /></SelectTrigger>
          <SelectContent>{reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Additional detail (optional)" className="bg-[#131B35] border-white/10 text-white" />
        <DialogFooter><Button data-testid="report-submit" onClick={submit} disabled={!reason} className="bg-rose-600 hover:bg-rose-700 text-white w-full">Submit Report</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
